import uniqBy from "lodash/uniqBy";

import { supabase } from "@/lib/supabase";
import { Tables } from "@/types/supabase-types";
import { CADCurrencyConverter } from "./currency";
import { calculateBaseQuantity, itemUOMSelect, ItemUOMSelectResult } from "./item-uoms";
import { Item, itemSelect, ItemSelectResult, processItem } from "./items";

export type WorkOrder = Omit<WorkOrderSelectResult, "lines"> & {
  receivedJobLines: number;
  jobLines: WorkOrderLineItem[];
  allLines: WorkOrderLineItemSelectResult[];
  linkedBuilds: LinkedBuildDetails[];
};

export interface WorkOrderLineItem extends Omit<WorkOrderLineItemSelectResult, "item"> {
  item: Item;
  /** Quantity for the line item converted to the base UOM */
  baseQuantity: number;
  linkedBuilds: LinkedBuildDetails[];
}

// TODO: Determine these types dynamically once TypeScript performance is good enough

// type WorkOrderSelectResult = SupabaseSelectResult<"WorkOrder", typeof workOrderSelect, WorkOrderSelectRelatedTables>;

// type WorkOrderSelectRelatedTables =
//   | "WorkOrderLineItem"
//   | "Item"
//   | "Job"
//   | ItemSelectRelatedTables;

// type WorkOrderLineItemSelectResult = WorkOrderSelectResult["lines"][0];

interface WorkOrderSelectResult extends Pick<
  Tables<"WorkOrder">,
  "id" | "number" | "date" | "comment" | "closed" | "archived"
> {
  lines: WorkOrderLineItemSelectResult[];
}

interface WorkOrderLineItemSelectResult extends Pick<
  Tables<"WorkOrderLineItem">,
  "id" | "lineNumber" | "description" | "produced" | "duedate"
> {
  /** Quantity for the line item in the selected ItemUOM */
  uomQuantity: number;
  item: ItemSelectResult;
  itemUOM: ItemUOMSelectResult | null;
  job: Pick<Tables<"Job">, "id" | "name"> | null;
  linkedBuildOutputLineItems: LinkedBuildOutputLineItem[];
}

interface LinkedBuildOutputLineItem extends Pick<Tables<"BuildOutputLineItem">, "quantity"> {
  build: LinkedBuildDetails;
}

type LinkedBuildDetails = Pick<Tables<"Build">, "id" | "number">;

const workOrderSelect = `
id,
number,
date,
comment,
closed,
archived,
lines:WorkOrderLineItem!inner(
  id,
  lineNumber,
  item:Item!inner(${itemSelect}),
  description,
  uomQuantity:quantity,
  itemUOM:ItemUOM!WorkOrderLineItem_itemId_uomId_fkey(${itemUOMSelect}),
  produced,
  duedate,
  job:Job(id, name),
  linkedBuildOutputLineItems:BuildOutputLineItem(
    quantity,
    build:Build!inner(id, number)
  )
)
`;

export async function getWorkOrders(jobId: number, CADCurrencyConverter: CADCurrencyConverter): Promise<WorkOrder[]> {
  // Get all WO ids based on non-deleted WO line items assigned to the job
  const jobMatchingWOIdsResponse = await supabase
    .from("WorkOrderLineItem")
    .select("workOrderId")
    .eq("jobId", jobId)
    .eq("deleted", false)
    .throwOnError();
  const jobMatchingWOIds = jobMatchingWOIdsResponse.data.map((x) => x.workOrderId);

  // Get the non-deleted WOs matching the returned ids
  const queryResult = (
    await supabase
      .from("WorkOrder")
      .select<typeof workOrderSelect, WorkOrderSelectResult>(workOrderSelect)
      .in("id", jobMatchingWOIds)
      .eq("deleted", false)
      .eq("lines.deleted", false)
      .order("date", { ascending: false })
      .order("lineNumber", { referencedTable: "lines", ascending: true })
      .throwOnError()
  ).data;

  // Do some additional client-side processing on the results before returning
  return queryResult.map((wo) => processWo(wo, jobId, CADCurrencyConverter));
}

function processWo(
  { lines: allLines, ...wo }: WorkOrderSelectResult,
  jobId: number,
  CADCurrencyConverter: CADCurrencyConverter,
): WorkOrder {
  // Get the job lines and process them
  const jobLines = allLines
    .filter((x) => x.job?.id === jobId)
    .map((line) => processWoLineItem(line, CADCurrencyConverter));

  // Get all linked builds for the WO (for the current job only)
  const linkedBuilds = uniqBy(
    jobLines.flatMap((line) => line.linkedBuilds),
    (x) => x.id,
  );

  // Count the number of job line items that have been fully produced
  const receivedJobLines = jobLines.filter((x) => x.produced === x.uomQuantity).length;

  // Return the processed work order
  return { ...wo, receivedJobLines, jobLines, allLines, linkedBuilds };
}

function processWoLineItem(
  line: WorkOrderLineItemSelectResult,
  CADCurrencyConverter: CADCurrencyConverter,
): WorkOrderLineItem {
  return {
    ...line,
    item: processItem(line.item, CADCurrencyConverter),
    baseQuantity: calculateBaseQuantity(line.uomQuantity, line.itemUOM),
    linkedBuilds: line.linkedBuildOutputLineItems.map((x) => x.build),
  };
}
